import { concat, find, forEach, get, map, replace } from 'lodash-es'
import * as moment from 'moment'
import { LBR, PBR } from './constants'
import { doPlainIndent, fieldText, freeTextSubHtmlBreaks, doPlainIndent_v2 } from './helpers'
import { outpatientInvestigations } from './investigation'
import { genericQuestionsText } from './question'
import { TextDetails, TextDetailsExt } from './interfaces'
import { Stay } from 'models/data/stay'
import { QuestionCategory } from "models/med_templates/question"
import stays from '@store/stays'
import store from 'store'


/**
 * 
 * @param param0 Returns the text for aimingFor
 */
export function aimingFor({ stay }: TextDetails): string {
    if (!stay) return ""

    const discharge = stay.discharge
    if (!discharge) return ""

    const speciality = stays.getSpeciality(stay.id)
    if (!speciality) return ""

    const aimingForChoices = speciality.aiming_for_choices || []
    const choice = find(aimingForChoices, ["id", discharge.aiming_for])

    if (choice) return choice.title

    return discharge.other_aiming_for || ""
}

export function aimingForMarkup(textDetails: TextDetails): string {
    const aimingForText = aimingFor(textDetails)
    return aimingForText ? `Aiming for: ${aimingForText}${PBR}` : ''
}

export function nextSteps({ stay }: TextDetails): string[] {
    if (!stay) return []

    const discharge = stay.discharge

    let nextStepText: string[] = []

    const speciality = stays.getSpeciality(stay.id)
    if (speciality && discharge && discharge.next_steps && discharge.next_steps.length) {
        const nextStepChoices = speciality.next_steps || []
        forEach(discharge.next_steps, function (id) {
            const choice = find(nextStepChoices, ["id", id])
            if (choice) {
                nextStepText.push(choice.title)
            }
        })
    }

    const otherNextSteps = get(discharge, "other_next_steps") || []
    if (otherNextSteps.length) {
        nextStepText = concat(nextStepText, map(otherNextSteps, "title"))
    }

    return nextStepText
}

export function readyWhen({ stay }: TextDetails): string[] {
    if (!stay) return []

    const discharge = stay.discharge

    let readyText: string[] = []

    const speciality = stays.getSpeciality(stay.id)
    if (speciality && discharge && discharge.ready_when && discharge.ready_when.length) {
        const readyWhenChoices = speciality.ready_for_discharge_when || []
        forEach(discharge.ready_when, function (id) {
            const choice = find(readyWhenChoices, ["id", id])
            if (choice) {
                readyText.push(choice.title)
            }
        })
    }

    const otherReadyWhen = get(discharge, "other_ready_when") || []
    if (otherReadyWhen.length) {
        readyText = concat(readyText, map(otherReadyWhen, "title"))
    }

    return readyText
}

function followUpIn({ stay }: TextDetails): string {
    if (!stay) return ""

    const discharge = stay.discharge
    if (!(discharge && discharge.follow_up_time)) return ""

    const speciality = stays.getSpeciality(stay.id)
    if (!speciality) return ""

    const followUpTime = discharge.follow_up_time
    const followUpTimeChoices = speciality.follow_up_times
    const followUpInObject = find(followUpTimeChoices, ["id", followUpTime]) || {}
    return get(followUpInObject, "title") || ""
}

/**
 * 
 * @param param0 
 * @param includePrefix Include "Follow up with " prefix text
 * @param includeInst Include instruction text, if available
 */
export function followUpWith({ stay }: TextDetails, includePrefix: boolean, includeInst: boolean): string {
    if (!stay) return ""

    const discharge = stay.discharge
    if (!(discharge && discharge.follow_up_with)) return ""

    let followUpText = ''
    let followUpWithText = ''
    let followUpInText = ''
    let instructionText = ''

    const followUpOptions = store.direct.state.templates.followUpWith
    const choice = find(followUpOptions, ["id", discharge.follow_up_with])

    if (choice && choice.title) {
        followUpWithText = includePrefix ? `Follow up with ${choice.title}` : choice.title
        if (choice.instruction)
            instructionText = `(${choice.instruction})`
    }
    followUpInText = followUpIn({ stay })

    if (followUpWithText)
        followUpText = followUpWithText
    if (followUpInText)
        followUpText += ` in ${followUpInText}`
    if (instructionText && includeInst)
        followUpText += ` ${instructionText}`

    return followUpText
}

export function medsChanged({ stay, plain }: TextDetailsExt): string {
    const meds_changed: string = stay?.discharge?.meds_changed
    if (!meds_changed) return ''
    let title = 'Medications changed'
    title = plain ? `${title}:` : `**${title}:**`
    let text = `${title}${LBR}${freeTextSubHtmlBreaks(meds_changed)}`
    return plain ? doPlainIndent(text) : text
}

function dischargePlanCYOAText({ stay, plain }: TextDetailsExt): string {
    if (!stay) return ''

    let dischargePlanGroupIDs: number[] = []
    // Any question groups that are attached to the stay's syndrome by default are valid too
    const syndrome = find(store.direct.getters.templates.allSyndromes, ['id', stay.syndrome])
    if (syndrome && syndrome.discharge_plans) {
        dischargePlanGroupIDs = dischargePlanGroupIDs.concat(syndrome.discharge_plans)
    }

    // Any question group IDs stored in `stay.question_groups` (i.e. added by user) are valid
    dischargePlanGroupIDs = dischargePlanGroupIDs.concat(stay.question_groups)

    return genericQuestionsText(
        {
            stay,
            category: QuestionCategory.DISCHARGE_PLAN,
            getQuestionGroupsFrom: stays.getSpeciality(stay.id),
            validQuestionGroupIDs: dischargePlanGroupIDs,
            orderByValid: true,
            doIndent: plain,
        }
    )
}

export function dischargePlan(textDetails: TextDetailsExt): string {
    const { stay, hideTitle, plain } = textDetails
    if (!stay) return ""

    const discharge = stay.discharge
    if (!discharge) return ""

    let lines: string[] = []

    if (discharge.aiming_for) {
        lines.push("Aiming for: " + aimingFor({ stay }))
    }

    if (discharge.estimated_discharge_date) {
        const date = moment(discharge.estimated_discharge_date)
        const formattedDate = date.format("Do MMM YYYY")
        lines.push(`Estimated discharge date: ${formattedDate}`)
    }

    const followUpText = followUpWith({ stay }, true, true)
    if (followUpText) lines.push(followUpText)

    if (discharge.blood_pressure_target) {
        const bpLookup: { [k: string]: string } = {
            NOT_DISCUSSED: "- not discussed",
            LT_130: "< 130 systolic following discharge",
            LT_140: "< 140 systolic following discharge",
            LT_150: "< 150 systolic following discharge",
        }
        lines.push(`Aiming for BP ${bpLookup[discharge.blood_pressure_target]}`)
    }

    if (discharge.driving_plan) {
        const drivingLookup: { [k: string]: string } = {
            NOT_DISCUSSED: "Not discussed",
            DOESNT_DRIVE: "Doesn\"t drive",
            NO_RESTRICTIONS: "No restrictions",
            NO_DRIVING_FOR_2_WEEKS: "No driving for 2 weeks",
            NO_DRIVING_FOR_4_WEEKS: "No driving for 4 weeks",
            NO_DRIVING_FOR_6_MONTHS: "No driving for 6 months",
            NO_DRIVING_UNTIL_REVIEW_BY_SPECIALIST: "No driving until review by specialist",
        }
        lines.push(`Driving: ${drivingLookup[discharge.driving_plan]}`)
    }

    const dischargeQuestionsText = dischargePlanCYOAText(textDetails)
    if (dischargeQuestionsText.length) {
        if (plain) lines.push('&ensp;')
        lines.push(dischargeQuestionsText)
    }

    const outpatientInvsText = outpatientInvestigations(textDetails)
    if (outpatientInvsText.length) {
        if (plain) lines.push('&ensp;')
        lines.push(outpatientInvsText)
    }

    if (discharge.notes) {
        if (plain) lines.push('&ensp;')
        lines.push(freeTextSubHtmlBreaks(discharge.notes))
    }

    if (!lines.length) return ""

    if (!hideTitle) {
        let title = 'Discharge Plan'
        title = plain ? `${title}:` : `**${title}:**`
        lines.unshift(title)
    }

    const text = lines.join(PBR)
    return (plain && !hideTitle) ? doPlainIndent_v2(text) : text
}

export function dischargePlanFinal(textDetails: TextDetailsExt): string {
    // Method used for text in the Discharge app's EMR notes
    const text = dischargePlan(textDetails)
    return replace(text, "Aiming for", "Discharge destination")
}

export function mdtDischargePlan({ stay }: TextDetails): string {
    let lines: string[] = []

    // Basic discharge plan text
    const text = dischargePlan({ stay })
    if (text.length) lines.push(text.trim())

    const nextStepsText = nextSteps({ stay })
    if (nextStepsText.length) {
        lines.push(`Next steps to progress discharge: ${nextStepsText.join(", ")}`)
    }

    const readyText = readyWhen({ stay })
    if (readyText.length) {
        lines.push(`Ready for discharge when: ${readyText.join(", ")}`)
    }

    if (lines.length) {
        return lines.join(LBR) + PBR
    }
    return ""
}

function _wardPlan(stay: Stay, planPath: string, plain: boolean = false): string {
    const planText = fieldText({ stay }, `ward_round.${planPath}`)
    if (planText.length === 0) return ''
    return plain ? doPlainIndent(`PLAN:${LBR}${planText}`) : `**Plan**  \n${planText}\n\n`
}

export function doctorWardPlan(stay: Stay, plain: boolean = false): string {
    return _wardPlan(stay, 'plan_notes', plain)
}

export function nurseWardPlan(stay: Stay, plain: boolean = false): string {
    return _wardPlan(stay, 'nurse_plan', plain)
}